import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { ExternalUser, SalesPerson } from "models/customers/customers";
import { Attachment, Subscriber } from "models/dashboard/dashboard";
import { DocumentUploadFile } from "models/DocumentUploadFile";
import { ThreadCategory } from "models/inbox/inbox";

export type FormValidation = {
  title: "EMPTY" | "VALID" | "INVALID";
  body: "EMPTY" | "VALID" | "INVALID";
  contentSize: "VALID" | "INVALID";
};

export interface MessageFormState {
  recipients: (SalesPerson | ExternalUser | Subscriber)[];
  title: string;
  text: string;
  attachments: Attachment[];
  computerAttachments: DocumentUploadFile[];
  selectedCategory: ThreadCategory;
  validation: FormValidation;
}

const initialFormState: MessageFormState = {
  recipients: [],
  title: "",
  text: "",
  attachments: [],
  computerAttachments: [],
  selectedCategory: ThreadCategory.None,
  validation: { title: "EMPTY", body: "EMPTY", contentSize: "VALID" }
};

export interface InboxReplyContextValue {
  isReplying: boolean;
  setIsReplying: (isReplying: boolean) => void;
  isDirty: boolean;
  markDirty: () => void;
  clearDirty: () => void;
  formState: MessageFormState;
  updateFormState: (updates: Partial<MessageFormState>) => void;
  resetFormState: (defaultRecipients?: (SalesPerson | ExternalUser | Subscriber)[], defaultTitle?: string) => void;
  formContextId: string | null;
  setFormContextId: (id: string | null) => void;
}

interface InboxReplyContextProps {
  children: ReactNode;
}

const InboxReplyContext = createContext<InboxReplyContextValue | undefined>(undefined);

export function InboxReplyContextProvider({ children }: InboxReplyContextProps) {
  const [isReplying, setIsReplying] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [formState, setFormState] = useState<MessageFormState>(initialFormState);
  const [formContextId, setFormContextId] = useState<string | null>(null);

  const markDirty = useCallback(() => setIsDirty(true), []);
  const clearDirty = useCallback(() => setIsDirty(false), []);

  const updateFormState = useCallback((updates: Partial<MessageFormState>) => {
    setFormState((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetFormState = useCallback((
    defaultRecipients?: (SalesPerson | ExternalUser | Subscriber)[],
    defaultTitle?: string
  ) => {
    setFormState({
      ...initialFormState,
      recipients: defaultRecipients?.map((r) => ({ ...r, isFixedRecipient: true })) ?? [],
      title: defaultTitle ?? ""
    });
  }, []);

  const value = useMemo(
    () => ({
      isReplying,
      setIsReplying,
      isDirty,
      markDirty,
      clearDirty,
      formState,
      updateFormState,
      resetFormState,
      formContextId,
      setFormContextId,
    }),
    [isReplying, isDirty, formState, markDirty, clearDirty, updateFormState, resetFormState, formContextId],
  );

  return (
    <InboxReplyContext.Provider value={value}>
      {children}
    </InboxReplyContext.Provider>
  );
}

export const useInboxReplyContext = () => {
  const context = useContext(InboxReplyContext);
  if (!context) {
    throw new Error(
      "useInboxReplyContext must be used within an InboxReplyContextProvider",
    );
  }
  return context;
};